import * as React from "react";
import { useState } from "react";
import { makeStyles, Button, Input, Text } from "@fluentui/react-components";
import { CheckmarkCircle16Filled, ErrorCircle16Filled } from "@fluentui/react-icons";

/**
 * Interface définissant les propriétés du composant MotDePasseOublierPage.
 */
export interface MotDePasseOublierProps {
  setCurrentView: (view: string) => void; // Fonction pour changer la vue actuelle
  sendPasswordRecoveryEmail: (Email: string) => Promise<{ message: string }>; // Fonction pour envoyer un email de récupération
  verifyRecoveryCode: (
    Email: string,
    Code: string,
    NewPassword: string
  ) => Promise<{ message: string }>; // Fonction pour vérifier le code de récupération et mettre à jour le mot de passe
  changingPassword: boolean; // Indique si l'utilisateur est en train de changer son mot de passe
}

/**
 * Composant MotDePasseOublierPage : permet à l'utilisateur de récupérer ou changer son mot de passe.
 * @param {MotDePasseOublierProps} props - Propriétés du composant.
 * @returns {JSX.Element} - Le composant React.
 */
const MotDePasseOublierPage: React.FC<MotDePasseOublierProps> = (props: MotDePasseOublierProps) => {
  // États locaux pour gérer les entrées utilisateur et l'état du composant
  const [errors, setErrors] = useState<string>(""); // Message d'erreur
  const [loading, setLoading] = useState<boolean>(false); // Indique si une requête est en cours
  const [success, setSuccess] = useState<boolean>(false); // Indique si l'email de récupération a été envoyé avec succès
  const [recoveryCompleted, setRecoveryCompleted] = useState<boolean>(false); // Indique si le processus de récupération est terminé
  const [email, setEmail] = useState<string>(""); // Adresse email de l'utilisateur
  const [confirmationCode, setConfirmationCode] = useState<string>(""); // Code de confirmation
  const [newPassword, setNewPassword] = useState<string>(""); // Nouveau mot de passe
  const [confirmNewPassword, setConfirmNewPassword] = useState<string>(""); // Confirmation du nouveau mot de passe

  const styles = useStyles();

  /**
   * Valide l'adresse email saisie.
   * @returns {boolean} - `true` si l'email est valide, sinon `false`.
   */
  const validateEmail = () => {
    const regex = /\S+@\S+\.\S+/;
    return regex.test(email);
  };

  /**
   * Valide le nouveau mot de passe et sa confirmation.
   * @returns {boolean} - `true` si le mot de passe est valide et correspond à la confirmation.
   */
  const validatePassword = () => {
    const strongPassword = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[\W_]).{8,}$/;
    return strongPassword.test(newPassword) && newPassword === confirmNewPassword;
  };

  /**
   * Envoie un email de récupération à l'utilisateur.
   */
  const finalizeSignup = async () => {
    setLoading(true);
    setErrors("");
    setSuccess(false);

    try {
      const res = await props.sendPasswordRecoveryEmail(email);

      if (res.message) {
        setSuccess(true);
        setErrors("");
      } else {
        setErrors("Une erreur est survenue, veuillez réessayer.");
      }
    } catch (error: any) {
      setErrors(error.message || "Une erreur est survenue, veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  /**
   * Vérifie le code de confirmation et met à jour le mot de passe.
   */
  const verifyCode = async () => {
    setLoading(true);
    setErrors("");

    if (!validatePassword()) {
      setErrors(
        "Veuillez entrer un mot de passe contenant plus de 8 caractères, 1 symbole et 1 nombre."
      );
      setLoading(false);
      return;
    }

    try {
      const result = await props.verifyRecoveryCode(email, confirmationCode, newPassword);
      if (result.message) {
        setRecoveryCompleted(true);
        setErrors("");
      }
    } catch (error: any) {
      setErrors(error.message || "Le code de confirmation est incorrect, veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  // Rendu du composant
  return (
    <div className={styles.container}>
      {/* Instructions pour l'utilisateur */}
      {props.changingPassword && (
        <p className={styles.text}>
          Pour changer votre mot de passe entrez votre adresse e-mail et nous vous enverrons un code
          de vérification.
        </p>
      )}
      {!props.changingPassword && (
        <p className={styles.text}>
          Oublié votre mot de passe? Entrez votre adresse e-mail et nous vous enverrons un code de
          récupération.
        </p>
      )}

      {/* Champ pour l'adresse email */}
      <p className={styles.title}>Email:</p>
      <Input
        type="email"
        className={styles.textAreaField}
        placeholder="email@example.com"
        value={email}
        onChange={(e: any) => setEmail(e.target.value)}
        disabled={success || recoveryCompleted}
      />

      {/* Bouton pour envoyer l'email de récupération */}
      <Button
        appearance="primary"
        className={
          loading || !validateEmail() || success || recoveryCompleted
            ? undefined
            : styles.greenButton
        }
        disabled={!validateEmail() || loading || success || recoveryCompleted}
        size="large"
        onClick={finalizeSignup}
      >
        {loading ? "Envoi..." : "Envoyer lien de récupération"}
      </Button>

      {/* Affiche les champs pour le code de confirmation et le nouveau mot de passe si l'email a été envoyé */}
      {success && (
        <>
          <p className={styles.title}>Entrez le code de confirmation:</p>
          <Input
            type="text"
            className={styles.textAreaField}
            placeholder="Code de confirmation"
            value={confirmationCode}
            onChange={(e: any) => setConfirmationCode(e.target.value)}
            disabled={recoveryCompleted}
          />
          <p className={styles.title}>Nouveau mot de passe:</p>
          <Input
            type="password"
            className={styles.textAreaField}
            placeholder="Nouveau mot de passe"
            value={newPassword}
            onChange={(e: any) => {
              setNewPassword(e.target.value);
              validatePassword();
            }}
            disabled={recoveryCompleted}
          />
          <p className={styles.title}>Confirmer le nouveau mot de passe:</p>
          <Input
            type="password"
            className={styles.textAreaField}
            placeholder="Confirmer le nouveau mot de passe"
            value={confirmNewPassword}
            onChange={(e: any) => {
              setConfirmNewPassword(e.target.value);
              validatePassword();
            }}
            disabled={recoveryCompleted}
          />
          <Button
            appearance="primary"
            className={
              loading || recoveryCompleted || !validatePassword() ? undefined : styles.greenButton
            }
            size="large"
            onClick={verifyCode}
            disabled={recoveryCompleted || !validatePassword()}
          >
            Vérifier le code
          </Button>
          <br />
          <br />
          {recoveryCompleted && (
            <Button
              appearance="primary"
              className={styles.greenButton}
              size="large"
              onClick={() => props.setCurrentView("Accueil")}
            >
              Se connecter
            </Button>
          )}
          {!validatePassword() && newPassword && confirmNewPassword && (
            <Text className={styles.errorText}>
              Veuillez entrer un mot de passe contenant plus de 8 caractères, 1 symbole et 1 nombre.
            </Text>
          )}
        </>
      )}

      {/* Affiche les messages d'erreur ou de succès */}
      {errors && (
        <div className={styles.errorText}>
          <ErrorCircle16Filled style={{ marginRight: "8px" }} />
          <Text>{errors}</Text>
        </div>
      )}
      {recoveryCompleted && (
        <div className={styles.successText}>
          <CheckmarkCircle16Filled style={{ marginRight: "8px" }} />
          <Text>Votre mot de passe a été mis à jour avec succès!</Text>
        </div>
      )}
      {success && !recoveryCompleted && (
        <div className={styles.successText}>
          <CheckmarkCircle16Filled style={{ marginRight: "8px" }} />
          <Text>L'email de récupération a été envoyé avec succès.</Text>
        </div>
      )}
    </div>
  );
};

export default MotDePasseOublierPage;

const useStyles = makeStyles({
  container: {
    padding: "20px",
    textAlign: "center",
    flexDirection: "column",
  },
  text: {
    fontSize: "15px",
  },
  title: {
    fontSize: "20px",
    fontWeight: "bold",
    textAlign: "left",
  },
  textAreaField: {
    maxHeight: "40px",
    width: "100%",
    marginBottom: "10px",
  },
  errorText: {
    color: "red",
    display: "flex",
    alignItems: "center",
    marginBottom: "12px",
  },
  successText: {
    color: "green",
    display: "flex",
    alignItems: "center",
    marginBottom: "12px",
  },
  greenButton: {
    backgroundColor: "#217346",
    color: "white",
    "&:hover": {
      backgroundColor: "#1e6d43",
    },
  },
});
